// Shift-schedule time helpers (2026-10-15). A schedule is planned in the
// BUSINESS's timezone (state.businessTimezone), whatever the device's own zone,
// so "Mon 6:30a" means 6:30 at the cafe. Shifts are stored as timestamptz.
// Calendar days here are plain "YYYY-MM-DD" strings (never `new Date(str)`,
// which would be midnight UTC - see CLAUDE.md "Calendar dates vs timestamps").
// Shared by the POS (controller / scheduleView) and the Time Clock.

const pad = (n) => String(n).padStart(2, "0");

// Wall-clock parts of an instant in `tz`.
export function zonedParts(date, tz) {
  const parts = {};
  new Intl.DateTimeFormat("en-US", {
    timeZone: tz, hourCycle: "h23",
    year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", weekday: "short",
  }).formatToParts(date).forEach((p) => { parts[p.type] = p.value; });
  return {
    y: +parts.year, m: +parts.month, d: +parts.day, h: +parts.hour, mi: +parts.minute,
    dateStr: `${parts.year}-${parts.month}-${parts.day}`,
    timeStr: `${parts.hour}:${parts.minute}`,
  };
}

const offsetMs = (utcMs, tz) => {
  const p = zonedParts(new Date(utcMs), tz);
  return Date.UTC(p.y, p.m - 1, p.d, p.h, p.mi) - Math.floor(utcMs / 60000) * 60000;
};

// "2026-10-19" + "06:30" at the cafe -> the real instant (DST-safe).
export function zonedToDate(dateStr, timeStr, tz) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const [h, mi] = timeStr.split(":").map(Number);
  const guess = Date.UTC(y, m - 1, d, h, mi);
  let ms = guess - offsetMs(guess, tz);
  ms = guess - offsetMs(ms, tz); // second pass lands on the right side of a DST change
  return new Date(ms);
}

export function addDays(dateStr, n) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + n));
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
}

// 0 = Monday ... 6 = Sunday
export function weekdayIndex(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  return (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7;
}

export function mondayOf(dateStr) {
  return addDays(dateStr, -weekdayIndex(dateStr));
}

export function todayIn(tz) {
  return zonedParts(new Date(), tz).dateStr;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function dayNum(dateStr) { return +dateStr.slice(8, 10); }

// "Oct 19 – 25" / "Oct 26 – Nov 1"
export function weekLabel(monday) {
  const sunday = addDays(monday, 6);
  const [, m1] = monday.split("-").map(Number);
  const [, m2] = sunday.split("-").map(Number);
  return m1 === m2
    ? `${MONTHS[m1 - 1]} ${dayNum(monday)} – ${dayNum(sunday)}`
    : `${MONTHS[m1 - 1]} ${dayNum(monday)} – ${MONTHS[m2 - 1]} ${dayNum(sunday)}`;
}

// "Fri Oct 23"
export function dayLabel(dateStr) {
  return `${WEEKDAYS[weekdayIndex(dateStr)]} ${MONTHS[+dateStr.slice(5, 7) - 1]} ${dayNum(dateStr)}`;
}

// "6:30a", "12p", "3:15p" - compact, for grid chips.
export function shortTime(date, tz) {
  const { h, mi } = zonedParts(date, tz);
  const suffix = h < 12 ? "a" : "p";
  const h12 = h % 12 || 12;
  return mi ? `${h12}:${pad(mi)}${suffix}` : `${h12}${suffix}`;
}

export function hoursBetween(start, end) {
  return (new Date(end) - new Date(start)) / 3600000;
}

// 5.5 -> "5.5h", 6 -> "6h"
export function fmtHours(h) {
  const r = Math.round(h * 100) / 100;
  return `${Number.isInteger(r) ? r : r.toFixed(r * 10 % 1 ? 2 : 1)}h`;
}

// ── Wall-clock "HH:MM" times (availability windows, 2026-10-17) ──
const toMin = (t) => { const [h, m] = t.split(":").map(Number); return h * 60 + m; };

// "09:00" -> "9a", "15:30" -> "3:30p"
export function clockShort(t) {
  const m = toMin(t), h = Math.floor(m / 60) % 24, mi = m % 60;
  return `${h % 12 || 12}${mi ? `:${pad(mi)}` : ""}${h < 12 ? "a" : "p"}`;
}

// Does a shift from `start` to `end` fit inside the window winStart-winEnd?
// An end at/before its start runs into the next day (both for shifts and windows).
export function fitsWindow(winStart, winEnd, start, end) {
  const ws = toMin(winStart), we = toMin(winEnd) + (toMin(winEnd) <= ws ? 1440 : 0);
  const s = toMin(start), e = toMin(end) + (toMin(end) <= s ? 1440 : 0);
  return (s >= ws && e <= we) || (s + 1440 >= ws && e + 1440 <= we);
}

// The business's zone for views that can't ask the model (2026-10-25). The controller sets it
// at login (setBusinessZone(model.businessTz())); until then the device's zone is used.
// Use these instead of `new Date()` local parts when a screen means "the cafe's today".
let _businessZone = null;
export function setBusinessZone(tz) { _businessZone = tz || null; }
export function businessZone() { return _businessZone || Intl.DateTimeFormat().resolvedOptions().timeZone; }
export function businessToday() { return todayIn(businessZone()); }
// Date/time text for an instant, in the business zone (opts as for toLocaleString).
export function bizFormat(date, opts) {
  return new Date(date).toLocaleString("en-US", { ...opts, timeZone: businessZone() });
}
